import { useCallback, useEffect, useState } from 'react';
import {
  EngineError,
  getInternalMessages,
  sendInternalMessage,
  type InternalMessage,
} from '../../api/engineDashboard';

interface Props {
  token: string;
  caseId: string;
  onUnauthorized: () => void;
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * Internal messaging (Danny, 2026-08-18): court staff <-> Levelheaded
 * staff about THIS case. Structurally separate from party comms and
 * loudly labeled so a mediator can never confuse the two. Judges can
 * write here by design — this composer deliberately ignores read-only.
 */
export function InternalMessagingPanel({ token, caseId, onUnauthorized }: Props) {
  const [messages, setMessages] = useState<InternalMessage[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getInternalMessages(token, caseId)
      .then((res) => setMessages(res.messages))
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setMessages([]);
        setError('Could not load internal messages.');
      });
  }, [token, caseId, onUnauthorized]);

  useEffect(load, [load]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await sendInternalMessage(token, caseId, body);
      setDraft('');
      load();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between">
        <div className="section-label">Internal messaging</div>
        <button type="button" className="text-xs text-gray-500 hover:text-gray-900" onClick={load}>
          Refresh
        </button>
      </div>
      <p className="mt-0.5 text-[11px] text-gray-400">
        Staff and court only — parties never see this.
      </p>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <div className="mt-2 max-h-72 space-y-2 overflow-y-auto">
        {messages === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="text-sm text-gray-500">No internal messages yet.</p>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="rounded-md border border-surface-border p-2">
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <span
                  className={
                    m.author_side === 'court'
                      ? 'rounded-full bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800'
                      : 'rounded-full bg-brand-subtle px-1.5 py-0.5 font-medium text-brand'
                  }
                >
                  {m.author_side === 'court' ? 'Court' : 'Levelheaded'}
                </span>
                <span className="font-medium text-gray-700">{m.author_name}</span>
                <span className="ml-auto">{fmt(m.created_at)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{m.body}</p>
            </div>
          ))
        )}
      </div>
      <div className="mt-2">
        <textarea
          className="w-full rounded-md border border-surface-border px-2 py-1.5 text-sm"
          rows={2}
          placeholder="Write to the case team…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
        />
        <button type="button" className="btn-primary mt-1.5 !py-1 !text-xs" disabled={busy || !draft.trim()} onClick={() => void send()}>
          {busy ? 'Sending…' : 'Send internal message'}
        </button>
      </div>
    </section>
  );
}
